/**
 * №29 (S2) — WHAT THE SUBJECT PUTS IN THE STAGE HEAD.
 *
 * The locked probe's `.crumb` is the same band on every mockup, and it carries three
 * things the shell cannot know on its own:
 *
 *   Vantage  /  Disclosure review   ● running · 18m          [Pause] [⤓ Export run]
 *   └ anchor  └ the WORKING TITLE   └ the LIVE STATE          └ the subject's verbs
 *
 * The anchor is the shell's (it comes off the matrix row, via `useAppScope`), and it
 * is the one thing that must NOT be restated per surface — that is the F1 defect
 * class. The other three belong to whatever is on the stage: only the run knows it
 * has been going 18 minutes, only the record knows it is closed, only the table knows
 * which view it is showing.
 *
 * So the subject DECLARES them, exactly as a route already declares its chat scope
 * (`useChatScope`, Sprint 22) — same shape, same lifecycle, same reason: the shell
 * owns the frame, the surface owns its own facts, and neither re-derives the other's.
 *
 * ## Two channels, and why the actions are a portal
 *
 * `useStageHead({ title, state })` is data: strings the head renders. Every field is
 * a primitive, so they are the effect's real dependencies (no ref written during
 * render — see the hook), and it CLEARS on unmount, so navigating away can never
 * leave the previous subject's live state on screen — the exact failure a stage head
 * is most likely to ship (a stale "● running" over a surface that is not running).
 *
 * `<StageActions>` is a PORTAL into the head's right-hand slot, not a ReactNode in
 * context. A subject's verbs are real controls with their own state (a disabled
 * "Pause" while a mutation is in flight, a menu that is open); storing elements in
 * context would re-render the whole shell on every one of those, and would put the
 * page's event handlers on the wrong side of the tree. Portalled, they stay the
 * page's own subtree and merely appear somewhere else — the same mechanism S1 used
 * to give one conversation two homes.
 *
 * D-L6: whatever a subject writes here is user-facing copy. Staff, crew, a named
 * role — never "agent".
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

/**
 * The live-state chip. `tone` drives the colour; `label` is the whole text and is
 * rendered verbatim, so a subject that cannot honestly say anything says nothing —
 * there is no default state, and no chip is rendered for an absent one (D-ST5: a
 * simulated liveness is worse than none).
 */
export interface StageState {
  tone: "running" | "warn" | "ok" | "neutral";
  label: string;
}

export interface StageHeadDecl {
  /** The subject's own working title — the probe's "/ Disclosure review". */
  title?: string | null;
  /** The live-state chip, when the subject actually has one. */
  state?: StageState | null;
}

/**
 * №29 (S4, §3 ④) — the counts beside the record's sub-nav rows, keyed by matrix row key
 * (`place-record-parties` → 1). Declared by the RECORD, rendered by the nav.
 *
 * This exists because ④ moved the record's views out of an in-page tab bar and into the
 * nav, and the tab bar was carrying counts ("Parties 1", "Files 0"). The probe draws them
 * too (`Files 312`, `Tasks 4`). Dropping them would have been a silent regression on the
 * surface this firm uses most — so they move with the rows rather than dying with the bar.
 *
 * It is the SAME channel as the crumb, deliberately: the nav is entity-generic and cannot
 * read a matter's parties, so the only honest source is the surface that already did. One
 * declaration mechanism, two things declared through it (R1) — not a second context that
 * happens to look like the first.
 */
export type SubViewCounts = Readonly<Record<string, number | undefined>>;

interface StageHeadValue extends StageHeadDecl {
  declare: (d: StageHeadDecl) => void;
  clear: () => void;
  /** The head's action slot. `<StageActions>` portals into it. */
  actionSlot: HTMLElement | null;
  setActionSlot: (el: HTMLElement | null) => void;
  /** Sub-nav counts declared by the record on the stage (§3 ④). */
  subViewCounts: SubViewCounts;
  declareCounts: (c: SubViewCounts) => void;
}

const Ctx = createContext<StageHeadValue | null>(null);

const NO_COUNTS: SubViewCounts = {};

export function StageHeadProvider({ children }: { children: ReactNode }) {
  const [decl, setDecl] = useState<StageHeadDecl>({});
  const [actionSlot, setActionSlot] = useState<HTMLElement | null>(null);
  const [subViewCounts, setSubViewCounts] = useState<SubViewCounts>(NO_COUNTS);
  const declare = useCallback((d: StageHeadDecl) => setDecl(d), []);
  const clear = useCallback(() => setDecl({}), []);
  const declareCounts = useCallback((c: SubViewCounts) => setSubViewCounts(c), []);
  const value = useMemo<StageHeadValue>(
    () => ({ ...decl, declare, clear, actionSlot, setActionSlot, subViewCounts, declareCounts }),
    [decl, declare, clear, actionSlot, subViewCounts, declareCounts],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function useStageHeadCtx(): StageHeadValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useStageHead must be used within a StageHeadProvider");
  return ctx;
}

/** What the shell's head reads. */
export function useStageHeadValue(): StageHeadDecl {
  const { title, state } = useStageHeadCtx();
  return { title, state };
}

/**
 * A subject declares its crumb, and the declaration is cleared on unmount so a stale
 * "● running" can never survive a navigation.
 *
 * `useChatScope` (the pattern this follows) holds its declaration in a ref that it
 * writes DURING RENDER, so a fresh object each render does not retrigger the effect.
 * That is a `react-hooks/refs` violation — `npx eslint` says so, and it is the same
 * class of thing that made the S1 hooks defect invisible to `tsc` and `vite`. It is
 * avoidable here rather than inherited: every field of a stage-head declaration is a
 * PRIMITIVE, so they can be the effect's real dependencies. No ref, no manual `deps`
 * argument, and the effect re-runs exactly when the words on screen change.
 */
export function useStageHead(decl: StageHeadDecl): void {
  const { declare, clear } = useStageHeadCtx();
  const title = decl.title ?? null;
  const tone = decl.state?.tone ?? null;
  const label = decl.state?.label ?? null;
  useEffect(() => {
    declare({ title, state: tone && label ? { tone, label } : null });
    return () => clear();
  }, [declare, clear, title, tone, label]);
}

/**
 * A record declares the counts beside its sub-nav rows (§3 ④), cleared on unmount so one
 * record's counts can never survive onto another's nav.
 *
 * The declaration is an OBJECT, unlike the crumb's primitives, so it cannot be its own
 * effect dependency — a fresh `{}` each render would re-declare forever. It is keyed on
 * a serialisation instead, which is a real dependency (it changes exactly when a count
 * changes) rather than a ref written during render, which is the `react-hooks/refs`
 * violation this file already declined to inherit once.
 */
export function useSubViewCounts(counts: SubViewCounts): void {
  const { declareCounts } = useStageHeadCtx();
  const key = JSON.stringify(counts);
  useEffect(() => {
    declareCounts(JSON.parse(key) as SubViewCounts);
    return () => declareCounts(NO_COUNTS);
  }, [declareCounts, key]);
}

/** What the nav reads to decorate the OPEN sub-nav. */
export function useSubViewCountsValue(): SubViewCounts {
  return useStageHeadCtx().subViewCounts;
}

/** The head's right-hand slot. Rendered once, by `SubjectHeader`. */
export function useStageActionSlot(): (el: HTMLElement | null) => void {
  const { setActionSlot } = useStageHeadCtx();
  return useCallback((el: HTMLElement | null) => setActionSlot(el), [setActionSlot]);
}

/**
 * The subject's verbs, rendered INTO the stage head. Used inside a routed page:
 *
 *   <StageActions><button className="stagebtn">Run again</button></StageActions>
 *
 * Renders nothing until the slot exists, which is one paint — the head mounts above
 * the outlet, so the slot is there before the page's second render.
 */
export function StageActions({ children }: { children: ReactNode }) {
  const { actionSlot } = useStageHeadCtx();
  return actionSlot ? createPortal(children, actionSlot) : null;
}
